<template>
  <div>
    <page-title>Content</page-title>

    <tabs v-if="$route.meta.isAdmin">
      <tabs-item :key="tabs.indexOf(tab)" v-for="tab in tabs" :link="tab.to">
        {{ tab.title }}
      </tabs-item>
    </tabs>

    <section-block>
      <template slot="header">
        <filter-bar
          resource="pages"
          :show-search="true"
          :searchOptions="{ draft: isDraftTab }"
        ></filter-bar>
      </template>

      <template slot="actions" v-if="$route.meta.isAdmin">
        <base-button
          :primary="true"
          @click="$router.push({ name: 'AdminContentPageCreate' })"
          v-if="userCanAdd"
          >New Page</base-button
        >
      </template>

      <div v-if="!loading">
        <vuetable
          :class="tableClassObject"
          :api-mode="false"
          :fields="table.fields"
          :data-manager="dataManager"
          @vuetable:row-clicked="onRowClicked"
          pagination-path
          ref="pageTable"
          @vuetable:pagination-data="onPaginationData"
          @tableUpdated="updateTable"
        >
          <template slot="date-created" slot-scope="props">
            <base-datetime
              :date="props.rowData.page.created_time"
              v-if="isDraftTab"
            ></base-datetime>
            <base-datetime
              :date="props.rowData.created_time"
              v-else
            ></base-datetime>
          </template>

          <template slot="review-date" slot-scope="props">
            <base-datetime
              :date="props.rowData.page.review_date"
              v-if="isDraftTab"
            ></base-datetime>
            <base-datetime
              :date="props.rowData.review_date"
              v-else
            ></base-datetime>
          </template>
          <template slot="last-modified" slot-scope="props">
            <span v-if="props.rowData.modified_time">
              <base-datetime
                :date="props.rowData.modified_time"
              ></base-datetime>
              <br />
              by {{ props.rowData.modified_username }}
            </span>
            <span v-else>(Page not modified)</span>
          </template>

          <template slot="parent-page" slot-scope="props">
            <span
              v-if="
                props.rowData.parent_data &&
                  props.rowData.parent_data &&
                  props.rowData.parent_data.title
              "
            >
              <router-link
                :to="`/content${props.rowData.parent_data.page_url}`"
                >{{ props.rowData.parent_data.title }}</router-link
              >
            </span>
            <span
              @click.prevent.stop
              v-else-if="
                props.rowData.page &&
                  props.rowData.page.parent_data &&
                  props.rowData.page.parent_data &&
                  props.rowData.page.parent_data.title
              "
            >
              <router-link
                :to="`/content${props.rowData.page.parent_data.page_url}`"
                >{{ props.rowData.page.parent_data.title }}</router-link
              >
            </span>
            <span v-else>(not set)</span>
          </template>
        </vuetable>
        <pagination table-pagination>
          <vuetable-pagination
            ref="pagination"
            @vuetable-pagination:change-page="onChangePage"
          ></vuetable-pagination>
        </pagination>
      </div>
      <loading v-else></loading>
    </section-block>
  </div>
</template>

<script>
import Vue from "vue";
import PageActions from "@/components/PageActions";
import Pagination from "@/components/pagination";
import VuetablePagination from "vuetable-2/src/components/VuetablePagination";
import UserHasPermission from "@/mixins/UserHasPermission";
import querySearch from "@/mixins/querySearch";

Vue.component("page-actions", PageActions);

export default {
  name: "AdminContent",

  components: { Pagination, VuetablePagination },

  mixins: [UserHasPermission, querySearch],

  data() {
    return {
      loading: false,
      mergedPages: [],
      draftFilter: ["drafts"],
      table: {
        fields: [
          {
            name: "title",
            title: "Title",
            width: "20%",
          },
          {
            name: "parent-page",
            title: "Parent Page",
            width: "20%",
            titleClass: "u-ta-center u-hide-mobile",
            dataClass: "u-ta-center u-hide-mobile",
          },
          {
            name: "date-created",
            title: "Date Created",
            width: "20%",
            titleClass: "u-ta-center u-hide-mobile",
            dataClass: "u-ta-center u-hide-mobile",
          },
          {
            name: "last-modified",
            title: "Last Modified",
            width: "20%",
            titleClass: "u-ta-center u-hide-mobile",
            dataClass: "u-ta-center u-hide-mobile",
          },
          {
            name: "review-date",
            title: "Review Date",
            width: "10%",
            titleClass: "u-ta-center u-hide-mobile",
            dataClass: "u-ta-center u-hide-mobile",
          },
          {
            name: PageActions,
            title: "Actions",
            width: "10%",
          },
        ],
      },
    };
  },

  beforeCreate() {
    this.$store.dispatch("pages/reset");
  },

  created() {
    const { tab } = this.$route.query;
    if (tab && tab !== "drafts") {
      this.$store.commit("pages/setTabFilter", tab);
    }

    this.loading = true;
    this.retrievePages(tab && this.draftFilter.includes(tab));
  },

  methods: {
    updateTable() {
      return true;
    },
    dataManager(sortOrder, pagination) {
      const returnObj = {
        ...this.$store.state.pages.pagination,
        data: this.mergedPages,
      };

      return returnObj;
    },
    retrievePages(draft = false) {
      this.loading = true;
      this.$store.commit("pages/setSearchQuery", "");
      this.$store.commit("pages/setPagination", true);
      this.setFiltersFromQuery("pages");
      this.$store
        .dispatch("pages/list", { draft })
        .then((response) => {
          this.mergedPages = response;
          this.loadDrafts()
            .then((data) => {
              if (data) {
                this.mergedPages = data;
              }
              this.loading = false;
            })
            .catch((err) => {
              this.loading = false;
              this.mergedPages = response;
            });
        })
        .catch((error) => {
          this.loading = false;
        });
    },

    async loadDrafts(pages = null) {
      if (!pages) {
        pages = this.pages;
      }
      // at this point, see if we can get items that are in draft and merge them into the main pagination data
      const hasDraft = pages.filter((item) => item.has_draft === true);

      if (hasDraft.length > 0) {
        const draftStories = await this.getDraftStories(hasDraft);
        const merged = this.mergeDrafts(draftStories, pages);
        return Promise.resolve(merged);
      } else {
        return Promise.resolve(pages);
      }
    },

    onRowClicked(row) {
      if (
        row.event.target.localName === "td" ||
        row.event.target.parentNode.localName === "td"
      ) {
        const pageId = row.data.page ? row.data.page.id : row.data.id;
        this.$router.push({ name: "AdminContentPageEdit", params: { pageId } });
      }
    },
    onPaginationData(paginationData) {
      this.$refs.pagination.setPaginationData(paginationData);
    },
    async getDraftStories(data) {
      const idset = data.map((item) => item.id);

      try {
        const results = await this.$store.dispatch(
          "pages/listDraftsById",
          idset
        );
        return Promise.resolve(results);
      } catch (err) {
        return Promise.reject(err);
      }
    },
    mergeDrafts(drafts, original) {
      const returnData = [];
      for (let i = 0; i < original.length; i++) {
        const id = original[i].id;
        const draft = drafts.filter((draft) => draft.page.id === id);

        if (draft.length > 0) {
          const {
            created_time,
            review_date,
            has_draft,
            ...draftItem
          } = draft[0];
          returnData.push({
            created_time: original[i].created_time,
            review_date: original[i].review_date,
            has_draft: original[i].has_draft,
            ...draftItem,
          });
        } else {
          returnData.push(original[i]);
        }
      }

      return returnData;
    },
    onChangePage(page) {
      const vm = this;
      const tableref = vm.$refs.pageTable;
      let nextPage;
      /**
       * Because why would the plugin send actual page numbers
       */
      switch (page) {
        case "next":
          nextPage = ++this.$store.state.pages.pagination.current_page;
          break;
        case "prev":
          nextPage = --this.$store.state.pages.pagination.current_page;
          break;
        default:
          nextPage = page;
          break;
      }
      this.$store
        .dispatch("pages/list", {
          page: nextPage,
          draft: this.draftFilter.includes(this.$route.query.tab),
        })
        .then((resp) => {
          window.scroll(0, 0);
          tableref.changePage(nextPage);
        });
    },
  },

  watch: {
    // refresh table when filtering
    pages(newValue, oldValue) {
      // this.mergedPages = newValue
      this.loadDrafts(newValue).then((data) => {
        this.mergedPages = data;
        //   this.loading = false;
        if (this.$refs.pageTable) {
          this.$refs.pageTable.refresh();
        }
      });
    },
  },

  computed: {
    isDraftTab() {
      return this.draftFilter.includes(this.$route.query.tab);
    },
    tableClassObject() {
      const { tab } = this.$route.query;

      return {
        "c-data-table": true,
        [`c-data-table--${tab}`]: tab,
      };
    },

    userCanAdd() {
      return this.userHasPermission("add_edit_pages");
    },

    pages() {
      return this.$store.state.pages.pages;
    },

    tabs() {
      const vm = this;
      return [
        {
          to: "/admin/content/pages",
          title: "All Pages",
        },
        {
          to: "/admin/content/pages?tab=drafts",
          title: "Drafts",
        },
        {
          to: "/admin/content/pages?tab=review",
          title: "For Review",
        },
        {
          to: "/admin/content/pages?tab=lapsed",
          title: "Lapsed",
        },
        {
          to: "/admin/content/pages?tab=pending",
          title: "Awaiting Approval",
        },
        {
          to: "/admin/content/pages?tab=rejected",
          title: "Rejected",
        },
      ];
    },
  },
};
</script>

<style lang="scss">
.p-content-admin {
  //
}
</style>
